/* De lijst van Locaties (/crm/sites): ophalen, tabs, zoeken, sorteren, de filter in
 * de URL, de volgorde voor vorige/volgende, en welk project bij een locatie zonder
 * project lijkt te horen. Zelfde opzet als de projectlijst en de offertelijst.
 *
 * Een Site (public.sites) is een opgeleverde installatie in beheer (CONTEXT.md). Er is
 * geen foreign key van sites.project_id naar projects, dus het project komt uit een
 * tweede vraag.
 *
 * Relatieve imports (geen @/), zodat de toetsen dit bestand zonder bundel lezen. */

export interface LijstClient {
  from(tabel: string): any;
}

export interface LijstProject {
  id: string;
  nummer: string | null;
  naam: string | null;
  organisatie: string | null;
  verwijderd: boolean;
}

export interface LijstLocatie {
  id: string;
  naam: string;
  nummer: string | null;
  adres: string | null;
  type: string | null;
  status: string | null;
  beschrijving: string | null;
  latitude: number | null;
  longitude: number | null;
  sharepoint: string | null;
  project_id: string | null;
  project: LijstProject | null;
  created_at: string;
  updated_at: string | null;
}

const KOLOMMEN = 'id, name, address, latitude, longitude, site_type, status, site_number, project_id, description, sharepoint_main_url, created_at, updated_at';
const getal = (v: unknown) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

/** Alle niet-verwijderde locaties, met hun project (nummer, naam, organisatie). */
export async function haalLocaties(client: LijstClient): Promise<LijstLocatie[]> {
  const { data, error } = await client.from('sites').select(KOLOMMEN).is('deleted_at', null);
  if (error) throw error;
  const rijen: any[] = data ?? [];
  const ids = [...new Set(rijen.map((r) => r.project_id).filter(Boolean))];
  const projecten = new Map<string, LijstProject>();
  if (ids.length) {
    // Geen embed: los, zodat een ontbrekende organisatie het project niet laat vallen.
    const { data: ps } = await client.from('projects').select('id, name, project_number, deleted_at, org_id').in('id', ids);
    const orgIds = [...new Set((ps ?? []).map((p: any) => p.org_id).filter(Boolean))];
    const orgs = new Map<string, string>();
    if (orgIds.length) {
      const { data: os } = await client.from('organizations').select('id, name').in('id', orgIds);
      for (const o of os ?? []) orgs.set(o.id, o.name);
    }
    for (const p of ps ?? []) {
      projecten.set(p.id, {
        id: p.id, nummer: p.project_number ?? null, naam: p.name ?? null,
        organisatie: p.org_id ? orgs.get(p.org_id) ?? null : null, verwijderd: Boolean(p.deleted_at),
      });
    }
  }
  return rijen.map((r) => ({
    id: String(r.id),
    naam: r.name ?? '(zonder naam)',
    nummer: r.site_number || null,
    adres: r.address || null,
    type: r.site_type ?? null,
    status: r.status ?? null,
    beschrijving: r.description || null,
    latitude: getal(r.latitude),
    longitude: getal(r.longitude),
    sharepoint: r.sharepoint_main_url || null,
    project_id: r.project_id ?? null,
    project: r.project_id ? projecten.get(r.project_id) ?? { id: r.project_id, nummer: null, naam: null, organisatie: null, verwijderd: false } : null,
    created_at: r.created_at,
    updated_at: r.updated_at ?? null,
  }));
}

/** De plaats uit een adres: het laatste deel na de komma, zonder postcode en land. */
export function plaatsUit(adres: string | null): string | null {
  if (!adres) return null;
  const delen = adres.split(',').map((d) => d.trim()).filter(Boolean).filter((d) => !/^(netherlands|nederland|the netherlands)$/i.test(d));
  if (delen.length < 2) return null;
  return delen.at(-1)!.replace(/^\d{4}\s?[A-Z]{2}\s+/, '').trim() || null;
}

/* ── tijd ─────────────────────────────────────────────────────────── */

/** '3 min', '2 uur', '4 dagen' sinds een tijdstip. */
export function geleden(iso: string | null, nu: Date = new Date()): string | null {
  if (!iso) return null;
  const ms = nu.getTime() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  const min = Math.max(0, Math.round(ms / 60_000));
  if (min < 60) return `${min} min`;
  const uur = Math.round(min / 60);
  if (uur < 48) return `${uur} uur`;
  return `${Math.round(uur / 24)} dagen`;
}


/* ── tabs ─────────────────────────────────────────────────────────── */

export const TABS = ['alle', 'actief', 'monitoring', 'inactief'] as const;
export type Tab = (typeof TABS)[number];

export const TAB_LABEL: Record<Tab, string> = {
  alle: 'Alle',
  actief: 'Actief',
  monitoring: 'Met monitoring',
  inactief: 'Inactief',
};

/** Actief is in bedrijf (ook in onderhoud); monitoring = er hangt een actieve EMS-locatie aan. */
export function inTab(l: LijstLocatie, tab: Tab, metEms: Set<string>): boolean {
  switch (tab) {
    case 'alle': return true;
    case 'actief': return l.status === 'active' || l.status === 'maintenance';
    case 'monitoring': return metEms.has(l.id);
    case 'inactief': return l.status === 'inactive';
  }
}

/* ── zoeken en sorteren ───────────────────────────────────────────── */

export type Sortering = 'nummer' | 'naam' | 'status';
export const SORTERINGEN: Sortering[] = ['nummer', 'naam', 'status'];
export type Weergave = 'lijst' | 'kaart';

export interface LijstFilter {
  zoek: string;
  tab: Tab;
  sortering: Sortering;
  oplopend: boolean;
  weergave: Weergave;
}

export const STANDAARD_FILTER: LijstFilter = { zoek: '', tab: 'alle', sortering: 'nummer', oplopend: true, weergave: 'lijst' };

export function passendBijZoek(l: LijstLocatie, zoek: string): boolean {
  const z = zoek.trim().toLowerCase();
  if (!z) return true;
  return [l.naam, l.nummer, l.adres, l.project?.nummer, l.project?.naam, l.project?.organisatie]
    .some((v) => v?.toLowerCase().includes(z));
}

/** S08 → 8; zonder nummer achteraan. */
const nummer = (l: LijstLocatie) => {
  const m = /(\d+)/.exec(l.nummer ?? '');
  return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
};
const STATUS_VOLGORDE = ['active', 'maintenance', 'inactive'];

export function filterEnSorteer(lijst: LijstLocatie[], f: LijstFilter, metEms: Set<string>): LijstLocatie[] {
  const uit = lijst.filter((l) => inTab(l, f.tab, metEms) && passendBijZoek(l, f.zoek));
  const richting = f.oplopend ? 1 : -1;
  const sleutel = (l: LijstLocatie): number | string => {
    switch (f.sortering) {
      case 'naam': return l.naam.toLowerCase();
      case 'status': {
        const i = STATUS_VOLGORDE.indexOf(l.status ?? '');
        return i === -1 ? 99 : i;
      }
      default: return nummer(l);
    }
  };
  return uit.sort((a, b) => {
    const x = sleutel(a);
    const y = sleutel(b);
    if (x < y) return -1 * richting;
    if (x > y) return 1 * richting;
    return nummer(a) - nummer(b) || a.naam.localeCompare(b.naam, 'nl');
  });
}

export interface TabTelling {
  tab: Tab;
  aantal: number;
}

/** Telling per tab binnen de zoekterm, zodat de tabs kloppen met wat je ziet. */
export function tellingen(lijst: LijstLocatie[], zoek: string, metEms: Set<string>): TabTelling[] {
  const basis = lijst.filter((l) => passendBijZoek(l, zoek));
  return TABS.map((tab) => ({ tab, aantal: basis.filter((l) => inTab(l, tab, metEms)).length }));
}

/* ── filter in de URL ─────────────────────────────────────────────── */

export function filterUitUrl(p: URLSearchParams): LijstFilter {
  const tab = p.get('tab');
  const sort = p.get('sort');
  return {
    zoek: p.get('q') ?? '',
    tab: TABS.includes(tab as Tab) ? (tab as Tab) : STANDAARD_FILTER.tab,
    sortering: SORTERINGEN.includes(sort as Sortering) ? (sort as Sortering) : STANDAARD_FILTER.sortering,
    oplopend: p.get('dir') !== 'desc',
    weergave: p.get('weergave') === 'kaart' ? 'kaart' : 'lijst',
  };
}

export function filterNaarUrl(f: LijstFilter): Record<string, string> {
  const uit: Record<string, string> = {};
  if (f.zoek) uit.q = f.zoek;
  if (f.tab !== STANDAARD_FILTER.tab) uit.tab = f.tab;
  if (f.sortering !== STANDAARD_FILTER.sortering) uit.sort = f.sortering;
  if (f.oplopend !== STANDAARD_FILTER.oplopend) uit.dir = f.oplopend ? 'asc' : 'desc';
  if (f.weergave !== STANDAARD_FILTER.weergave) uit.weergave = f.weergave;
  return uit;
}

/* ── terug naar de lijst, en vorige/volgende op de pagina ──────────── */

const ZOEK_SLEUTEL = 'locaties:zoek';
const VOLGORDE_SLEUTEL = 'locaties:volgorde';

function opslag(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

export function bewaarLijst(zoek: string, volgorde: string[]): void {
  try {
    opslag()?.setItem(ZOEK_SLEUTEL, zoek ? `?${zoek}` : '');
    opslag()?.setItem(VOLGORDE_SLEUTEL, JSON.stringify(volgorde));
  } catch {
    /* privévenster of geen opslag: dan terug naar de lijst zonder filter, en vorige/volgende op nummer */
  }
}

export function lijstAdres(): string {
  try {
    return `/crm/sites${opslag()?.getItem(ZOEK_SLEUTEL) ?? ''}`;
  } catch {
    return '/crm/sites';
  }
}

export function leesVolgorde(): string[] | null {
  try {
    const v = JSON.parse(opslag()?.getItem(VOLGORDE_SLEUTEL) ?? 'null');
    return Array.isArray(v) && v.every((x) => typeof x === 'string') ? v : null;
  } catch {
    return null;
  }
}

/** Vorige en volgende rond `id`; null aan de randen of als id er niet in staat. */
export function buren(volgorde: string[], id: string): { vorige: string | null; volgende: string | null; positie: number; totaal: number } | null {
  const i = volgorde.indexOf(id);
  if (i === -1) return null;
  return { vorige: volgorde[i - 1] ?? null, volgende: volgorde[i + 1] ?? null, positie: i + 1, totaal: volgorde.length };
}

/* ── welk project hoort erbij ─────────────────────────────────────── */

export interface KandidaatProject {
  id: string;
  project_number: string | null;
  name: string;
}

const kaal = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/**
 * Het project waar een locatie zonder project bij lijkt te horen: hetzelfde nummer
 * (S08 ↔ P08) of dezelfde naam. Alleen een voorstel; koppelen doet de gebruiker.
 * Null als er al een project is, of als er niet precies één kandidaat is.
 */
export function projectVoorstel(
  l: { nummer: string | null; naam: string; project_id: string | null },
  projecten: KandidaatProject[],
): KandidaatProject | null {
  if (l.project_id) return null;
  const n = /^S0*(\d+)$/i.exec(l.nummer ?? '')?.[1];
  if (n) {
    const opNummer = projecten.filter((p) => /^P0*(\d+)$/i.exec(p.project_number ?? '')?.[1] === n);
    if (opNummer.length === 1) return opNummer[0]!;
  }
  const naam = kaal(l.naam);
  const opNaam = projecten.filter((p) => kaal(p.name) === naam);
  return opNaam.length === 1 ? opNaam[0]! : null;
}

/** Koppelt een locatie aan een project, alleen als ze er nog geen heeft (niets overschrijven). */
export async function koppelProject(client: LijstClient, siteId: string, projectId: string): Promise<void> {
  const { data, error } = await client
    .from('sites')
    .update({ project_id: projectId })
    .eq('id', siteId)
    .is('project_id', null)
    .select('id');
  if (error) throw error;
  if (!data?.length) throw new Error('Deze locatie is intussen al aan een project gekoppeld, of je mag hem niet wijzigen. Laad de pagina opnieuw.');
}
